import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaProjectDiagram, 
  FaSave, 
  FaTimes, 
  FaUsers, 
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaFileAlt,
  FaArrowLeft,
  FaUserPlus,
  FaTrash
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FormIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1rem;
  box-shadow: 0 6px 18px rgba(246, 139, 33, 0.3);
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const FormSubtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const FormContent = styled.div`
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #141340;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input<{ readOnly?: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#e0e0e0')};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: ${({ readOnly }) => (readOnly ? '#f8f9fa' : 'white')};
  color: ${({ readOnly }) => (readOnly ? '#666' : '#333')};
  cursor: ${({ readOnly }) => (readOnly ? 'not-allowed' : 'text')};
  
  &:focus {
    outline: none;
    border-color: ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#F68B21')};
    box-shadow: ${({ readOnly }) => (readOnly ? 'none' : '0 0 0 3px rgba(246, 139, 33, 0.1)')};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select<{ readOnly?: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#e0e0e0')};
  border-radius: 8px;
  font-size: 1rem;
  background: ${({ readOnly }) => (readOnly ? '#f8f9fa' : 'white')};
  cursor: ${({ readOnly }) => (readOnly ? 'not-allowed' : 'pointer')};
  color: ${({ readOnly }) => (readOnly ? '#666' : '#333')};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#F68B21')};
    box-shadow: ${({ readOnly }) => (readOnly ? 'none' : '0 0 0 3px rgba(246, 139, 33, 0.1)')};
  }
`;

const Textarea = styled.textarea<{ readOnly?: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#e0e0e0')};
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: ${({ readOnly }) => (readOnly ? 'none' : 'vertical')};
  min-height: 100px;
  transition: all 0.3s ease;
  background-color: ${({ readOnly }) => (readOnly ? '#f8f9fa' : 'white')};
  color: ${({ readOnly }) => (readOnly ? '#666' : '#333')};
  cursor: ${({ readOnly }) => (readOnly ? 'not-allowed' : 'text')};
  
  &:focus {
    outline: none;
    border-color: ${({ readOnly }) => (readOnly ? '#e0e0e0' : '#F68B21')};
    box-shadow: ${({ readOnly }) => (readOnly ? 'none' : '0 0 0 3px rgba(246, 139, 33, 0.1)')};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;
  margin-top: 2rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
`;

const AlertMessage = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ type }) => {
    switch (type) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'warning':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
    }
  }}
`;

const StaffSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 2px solid #e0e0e0;
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StaffCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #F68B21;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StaffHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const StaffAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
`;

const StaffInfo = styled.div`
  flex: 1;
`;

const StaffName = styled.div`
  font-weight: 600;
  color: #141340;
  font-size: 0.9rem;
`;

const StaffRole = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const StaffContact = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #666;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const AddStaffSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 2px dashed #e0e0e0;
`;

const AddStaffForm = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
`;

const AddStaffSelect = styled.select`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const SmallButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

interface ProjectFormData {
  name: string;
  description: string;
  status: string;
  budget: number;
  startDate: string;
  endDate: string;
  location: string;
  type: string;
  teamSize: number;
  assignedStaff: Array<{
    name: string;
    role: string;
    email: string;
    phone: string;
    employeeId: string;
  }>;
}

interface ProjectFormErrors {
  name?: string;
  description?: string;
  status?: string;
  budget?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  type?: string;
  teamSize?: string;
}

// Mock project data for editing
const mockProjects = {
  'PRJ-001': {
    name: 'Road Construction Project',
    description: 'Construction of main highway connecting two major cities with modern infrastructure and safety features.',

    status: 'in-progress',
    budget: 5000000,
    startDate: '2023-01-15',
    endDate: '2024-06-30',
    location: 'Dar es Salaam - Arusha',
    type: 'Infrastructure',
    teamSize: 45,
    assignedStaff: [
      {
        name: 'John Doe',
        role: 'Supervisor',
        email: 'john.doe@example.com',
        phone: '+255 123 456 789',
        employeeId: 'EMP-001'
      },
      {
        name: 'Jane Smith',
        role: 'Project Manager',
        email: 'jane.smith@example.com',
        phone: '+255 123 456 790',
        employeeId: 'EMP-002'
      }
    ]
  },
  'PRJ-002': {
    name: 'School Renovation',
    description: 'Renovation of primary school facilities including classrooms, library, and playground.',

    status: 'completed',
    budget: 1200000,
    startDate: '2023-03-01',
    endDate: '2023-12-15',
    location: 'Mwanza',
    type: 'Education',
    teamSize: 12,
    assignedStaff: [
      {
        name: 'Emily Davis',
        role: 'Store-Keeper (Logistics)',
        email: 'emily.davis@example.com',
        phone: '+255 123 456 791',
        employeeId: 'EMP-003'
      }
    ]
  },
  'PRJ-003': {
    name: 'Water Supply System',
    description: 'Installation of water supply system for rural community with modern filtration.',

    status: 'draft',
    budget: 2500000,
    startDate: '2024-02-01',
    endDate: '2024-12-31',
    location: 'Dodoma',
    type: 'Infrastructure',
    teamSize: 20,
    assignedStaff: []
  }
};

// Mock available staff data - using valid system roles
const availableStaff = [
  { name: 'John Doe', role: 'Supervisor', email: 'john.doe@example.com', phone: '+255 123 456 789', employeeId: 'EMP-001' },
  { name: 'Jane Smith', role: 'Project Manager', email: 'jane.smith@example.com', phone: '+255 123 456 790', employeeId: 'EMP-002' },
  { name: 'Emily Davis', role: 'Store-Keeper (Logistics)', email: 'emily.davis@example.com', phone: '+255 123 456 791', employeeId: 'EMP-003' },
  { name: 'Mike Wilson', role: 'Cashier', email: 'mike.wilson@example.com', phone: '+255 123 456 792', employeeId: 'EMP-004' },
  { name: 'Robert Brown', role: 'Procurement', email: 'robert.brown@example.com', phone: '+255 123 456 793', employeeId: 'EMP-005' },
  { name: 'Lisa Anderson', role: 'Supervisor', email: 'lisa.anderson@example.com', phone: '+255 123 456 794', employeeId: 'EMP-006' }
];

const HRProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    
    status: 'draft',
    budget: 0,
    startDate: '',
    endDate: '',
    location: '',
    type: '',
    teamSize: 0,
    assignedStaff: []
  });
  
  const [errors, setErrors] = useState<ProjectFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning', message: string } | null>(null);
  const [selectedStaffToAdd, setSelectedStaffToAdd] = useState('');

  // Load project data for editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      
      // Simulate API call to load project data
      setTimeout(() => {
        const projectData = mockProjects[id as keyof typeof mockProjects];
        
        if (projectData) {
          setFormData(projectData);
        } else {
          setAlertMessage({ type: 'error', message: 'Project not found. Please check the project ID.' });
        }
        
        setIsLoading(false);
      }, 1000);
    }
  }, [isEditing, id]);

  const getStaffInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };

  const handleAddStaff = () => {
    if (!selectedStaffToAdd) {
      setAlertMessage({ type: 'error', message: 'Please select a staff member to add.' });
      return;
    }

    const staffToAdd = availableStaff.find(staff => staff.employeeId === selectedStaffToAdd);
    if (staffToAdd && !formData.assignedStaff.find(staff => staff.employeeId === staffToAdd.employeeId)) {
      setFormData(prev => ({
        ...prev,
        assignedStaff: [...prev.assignedStaff, staffToAdd]
      }));
      setSelectedStaffToAdd('');
      setAlertMessage({ type: 'success', message: `${staffToAdd.name} has been added to the project.` });
      
      // Clear success message after 3 seconds
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const handleRemoveStaff = (employeeId: string) => {
    const staffToRemove = formData.assignedStaff.find(staff => staff.employeeId === employeeId);
    if (staffToRemove) {
      setFormData(prev => ({
        ...prev,
        assignedStaff: prev.assignedStaff.filter(staff => staff.employeeId !== employeeId)
      }));
      setAlertMessage({ type: 'success', message: `${staffToRemove.name} has been removed from the project.` });
      
      // Clear success message after 3 seconds
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Success message
      setAlertMessage({ 
        type: 'success', 
        message: 'Project staff assignments updated successfully!' 
      });
      
      // Navigate back to projects list after a short delay
      setTimeout(() => {
        navigate('/hr/projects');
      }, 2000);
      
    } catch (error) {
      setAlertMessage({ 
        type: 'error', 
        message: 'Failed to update project staff assignments. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/hr/projects');
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner>
          Loading project data...
        </LoadingSpinner>
      </Container>
    );
  }

  // Get available staff that are not already assigned
  const availableStaffToAssign = availableStaff.filter(
    staff => !formData.assignedStaff.find(assigned => assigned.employeeId === staff.employeeId)
  );

  return (
    <Container>
      {alertMessage && (
        <AlertMessage type={alertMessage.type}>
          {alertMessage.message}
        </AlertMessage>
      )}

      <PageHeader>
        <PageTitle>
          <IconWrapper icon={FaProjectDiagram} />
          Manage Project Staff
        </PageTitle>
        <ActionButtons>
          <Button variant="secondary" onClick={handleCancel}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Projects
          </Button>
        </ActionButtons>
      </PageHeader>

      <FormContainer>
        <FormHeader>
          <FormIcon>
            <IconWrapper icon={FaProjectDiagram} />
          </FormIcon>
          <FormTitle>Manage Staff Assignments</FormTitle>
          <FormSubtitle>
            Update staff assignments for {formData.name}
          </FormSubtitle>
        </FormHeader>

        <FormContent>
          <form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>
                <IconWrapper icon={FaFileAlt} />
                Project Information (Read Only)
              </SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaProjectDiagram} />
                    Project Name
                  </Label>
                  <Input
                    type="text"
                    value={formData.name}
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaProjectDiagram} />
                    Project Type
                  </Label>
                  <Input
                    type="text"
                    value={formData.type}
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaFileAlt} />
                    Description
                  </Label>
                  <Textarea
                    value={formData.description}
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaMapMarkerAlt} />
                    Location
                  </Label>
                  <Input
                    type="text"
                    value={formData.location}
                    readOnly
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>
                <IconWrapper icon={FaUsers} />
                Project Details (Read Only)
              </SectionTitle>
              <FormGrid>


                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaUsers} />
                    Team Size
                  </Label>
                  <Input
                    type="number"
                    value={formData.teamSize}
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaProjectDiagram} />
                    Status
                  </Label>
                  <Input
                    type="text"
                    value={formData.status}
                    readOnly
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>
                <IconWrapper icon={FaCalendarAlt} />
                Timeline (Read Only)
              </SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaCalendarAlt} />
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaCalendarAlt} />
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    readOnly
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>
                <IconWrapper icon={FaMoneyBillWave} />
                Budget Information (Read Only)
              </SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>
                    <IconWrapper icon={FaMoneyBillWave} />
                    Total Budget (TZS)
                  </Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    readOnly
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <StaffSection>
              <SectionTitle>
                <IconWrapper icon={FaUsers} />
                Assigned Staff ({formData.assignedStaff.length} members)
              </SectionTitle>
              
              {formData.assignedStaff.length > 0 ? (
                <StaffGrid>
                  {formData.assignedStaff.map((staff, index) => (
                    <StaffCard key={index}>
                      <StaffHeader>
                        <StaffAvatar>
                          {getStaffInitials(staff.name)}
                        </StaffAvatar>
                        <StaffInfo>
                          <StaffName>{staff.name}</StaffName>
                          <StaffRole>{staff.role}</StaffRole>
                        </StaffInfo>
                        <SmallButton 
                          variant="danger" 
                          onClick={() => handleRemoveStaff(staff.employeeId)}
                          type="button"
                        >
                          <IconWrapper icon={FaTrash} />
                        </SmallButton>
                      </StaffHeader>
                      <StaffContact>
                        <ContactItem>
                          <IconWrapper icon={FaFileAlt} style={{ fontSize: '0.7rem' }} />
                          {staff.employeeId}
                        </ContactItem>
                        <ContactItem>
                          <IconWrapper icon={FaFileAlt} style={{ fontSize: '0.7rem' }} />
                          {staff.email}
                        </ContactItem>
                        <ContactItem>
                          <IconWrapper icon={FaFileAlt} style={{ fontSize: '0.7rem' }} />
                          {staff.phone}
                        </ContactItem>
                      </StaffContact>
                    </StaffCard>
                  ))}
                </StaffGrid>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666', fontStyle: 'italic' }}>
                  No staff assigned to this project
                </div>
              )}

              {availableStaffToAssign.length > 0 && (
                <AddStaffSection>
                  <Label>
                    <IconWrapper icon={FaUserPlus} />
                    Add Staff Member
                  </Label>
                  <AddStaffForm>
                    <AddStaffSelect
                      value={selectedStaffToAdd}
                      onChange={(e) => setSelectedStaffToAdd(e.target.value)}
                    >
                      <option value="">Select staff member to add...</option>
                      {availableStaffToAssign.map(staff => (
                        <option key={staff.employeeId} value={staff.employeeId}>
                          {staff.name} - {staff.role}
                        </option>
                      ))}
                    </AddStaffSelect>
                    <SmallButton variant="primary" onClick={handleAddStaff} type="button">
                      <IconWrapper icon={FaUserPlus} />
                      Add Staff
                    </SmallButton>
                  </AddStaffForm>
                </AddStaffSection>
              )}
            </StaffSection>

            <FormActions>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                <IconWrapper icon={FaTimes} />
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                <IconWrapper icon={FaSave} />
                {isSubmitting ? 'Saving...' : 'Update Staff Assignments'}
              </Button>
            </FormActions>
          </form>
        </FormContent>
      </FormContainer>
    </Container>
  );
};

export default HRProjectForm; 